import React from 'react';

interface SummaryRow {
  label: string;
  value: string;
  icon?: string;
}

interface SummaryElement {
  type: string;
  text?: string;
  label?: string;
  value?: string;
  location?: string;
  sequence: number;
  row_type?: string;
  metadata?: Record<string, any>;
}

interface StructuredSummary {
  intro_text?: string;
  summary_rows?: SummaryRow[];
  footer_text?: string;
  excluded_items?: string[];
  excluded_locations?: string[];
  elements?: SummaryElement[];
}

const hasTrailContentTypes = (elements: SummaryElement[]): boolean => {
  const contentTypesRow = elements.find(e => e.type === 'row' && e.row_type === 'content_types');
  if (!contentTypesRow || !contentTypesRow.metadata) {
    return true;
  }
  const targetContentTypes = contentTypesRow.metadata.target_content_types;
  if (!targetContentTypes || !Array.isArray(targetContentTypes) || targetContentTypes.length === 0) {
    return true;
  }
  return targetContentTypes.some(type => type === 'bike_trails' || type === 'hiking_trails');
};

export const SummaryRenderer: React.FC<{ data: StructuredSummary; isStreaming?: boolean }> = ({ data, isStreaming = false }) => {
  // Handle both old format and new streaming format
  const hasElements = data.elements && data.elements.length > 0;
  
  if (hasElements) {
    const elements = data.elements!.sort((a, b) => a.sequence - b.sequence);
    const intro = elements.find(e => e.type === 'intro');
    let rows = elements.filter(e => e.type === 'row');
    const excludedElements = elements.filter(e => e.type === 'excluded');
    const footer = elements.find(e => e.type === 'footer');
    
    // Separate weather rows from context rows
    const weatherRows = rows.filter(row => row.row_type === 'weather');
    const contextRows = rows.filter(row => row.row_type !== 'weather');
    
    // Filter out context rows with empty or undefined values
    let filteredContextRows = contextRows.filter(row => 
      row.value && row.value.trim() !== '' && 
      row.value.toLowerCase() !== 'none' && 
      row.value.toLowerCase() !== 'not specified'
    );
    
    // Filter out difficulty row if no trails are present
    if (!hasTrailContentTypes(elements)) {
      filteredContextRows = filteredContextRows.filter(row => row.row_type !== 'difficulty');
    }
    
    // Filter weather rows - show all non-empty values (including "Not available" which is valuable information)
    const filteredWeatherRows = weatherRows.filter(row => 
      row.value && row.value.trim() !== ''
    );
    
    // Only apply streaming animation if actively streaming
    const animationClass = isStreaming ? 'streaming-element' : '';
    
    return (
      <div className="space-y-3">
        {intro && intro.text && (
          <div className={`text-sm md:text-base text-gray-800 leading-relaxed ${animationClass}`}>
            {intro.text}
          </div>
        )}
        
        {/* Context Card - Main search requirements */}
        <div className="bg-slate-50/50 border border-slate-200 rounded-lg p-3 md:p-4 space-y-2.5">
          {filteredContextRows.map((row, index) => (
            <div key={index} className={`flex items-start gap-2 ${animationClass}`} style={isStreaming ? { animationDelay: `${index * 50}ms` } : undefined}>
              <span className="font-semibold text-gray-800 flex-shrink-0 text-sm md:text-base min-w-[100px] md:min-w-[120px]">
                {row.label}:
              </span>
              <span className="text-gray-700 leading-relaxed text-sm md:text-base">
                {row.value}
              </span>
            </div>
          ))}
        </div>
        
        {/* Weather Section - Additional clarifying information */}
        {filteredWeatherRows.length > 0 && (
          <div className="bg-blue-50/50 border border-blue-200 rounded-lg p-3 md:p-4">
            {filteredWeatherRows.map((row, index) => (
              <div key={index} className={`flex items-start gap-2 ${animationClass}`} style={isStreaming ? { animationDelay: `${(filteredContextRows.length + index) * 50}ms` } : undefined}>
                <span className="font-semibold text-blue-800 flex-shrink-0 text-sm md:text-base min-w-[100px] md:min-w-[120px]">
                  {row.label}:
                </span>
                <span className="text-blue-700 leading-relaxed text-sm md:text-base">
                  {row.value}
                </span>
              </div>
            ))}
          </div>
        )}
        
        {excludedElements.length > 0 && (
          <div className={`bg-red-50 border border-red-200 rounded-lg p-3 text-xs md:text-sm space-y-1.5 ${animationClass}`}>
            <div>
              <span className="font-semibold text-red-800">Excluded locations:</span>
              <span className="ml-1 text-red-700">
                {excludedElements.map(e => e.location).join(', ')}
              </span>
            </div>
          </div>
        )}
        
        {footer && footer.text && (
          <div className={`text-sm md:text-base text-gray-800 font-semibold leading-relaxed mt-4 pt-3 border-t border-gray-200 ${animationClass}`}>
            {footer.text}
          </div>
        )}
      </div>
    );
  }
  
  // Original format (backward compatibility)
  const hasExcludedItems = data.excluded_items && data.excluded_items.length > 0;
  const hasExcludedLocations = data.excluded_locations && data.excluded_locations.length > 0;

  // Filter out rows with empty or undefined values
  const filteredRows = data.summary_rows ? data.summary_rows.filter(row => 
    row.value && row.value.trim() !== '' && row.value.toLowerCase() !== 'none' && row.value.toLowerCase() !== 'not specified'
  ) : [];

  return (
    <div className="space-y-3">
      {/* Introduction */}
      <div className="text-sm md:text-base text-gray-800 leading-relaxed">
        {data.intro_text}
      </div>
      
      {/* Summary Card - Modern card-based layout */}
      <div className="bg-slate-50/50 border border-slate-200 rounded-lg p-3 md:p-4 space-y-2.5">
        {filteredRows.map((row, index) => (
          <div key={index} className="flex items-start gap-2">
            <span className="font-semibold text-gray-800 flex-shrink-0 text-sm md:text-base min-w-[100px] md:min-w-[120px]">
              {row.label}:
            </span>
            <span className="text-gray-700 leading-relaxed text-sm md:text-base">
              {row.value}
            </span>
          </div>
        ))}
      </div>
      
      {/* Exclusions */}
      {(hasExcludedItems || hasExcludedLocations) && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs md:text-sm space-y-1.5">
          {hasExcludedItems && (
            <div>
              <span className="font-semibold text-red-800">Excluded items:</span>
              <span className="ml-1 text-red-700">{data.excluded_items!.join(', ')}</span>
            </div>
          )}
          {hasExcludedLocations && (
            <div>
              <span className="font-semibold text-red-800">Excluded locations:</span>
              <span className="ml-1 text-red-700">{data.excluded_locations!.join(', ')}</span>
            </div>
          )}
        </div>
      )}

      {/* Footer question - Visually distinct */}
      <div className="text-sm md:text-base text-gray-800 font-semibold leading-relaxed mt-4 pt-3 border-t border-gray-200">
        {data.footer_text}
      </div>
    </div>
  );
};